import { useEffect } from 'react'

/**
 * Piège de focus + restitution, pour un dialogue modal.
 *
 * `aria-modal` ne fait RIEN au parcours clavier : sans piège, Tab quitte la
 * modale et parcourt la page dessous, qui reste pourtant inaccessible à la
 * souris (WCAG 2.4.3 / 2.1.2). Et sans restitution, fermer la modale renvoie le
 * focus au `<body>` — l'utilisateur au clavier repart du début de la page au
 * lieu du bouton qu'il venait d'actionner.
 *
 * @param {import('react').RefObject<HTMLElement>} ref
 *   Le panneau du dialogue.
 * @param {boolean} active
 *   Faux → le hook ne fait rien (dialogue non monté / désactivé).
 */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',')

export const useDialogFocus = (ref, active = true) => {
  useEffect(() => {
    if (!active) return
    const panel = ref.current
    if (!panel) return

    // Mémoriser AVANT de déplacer le focus : c'est là qu'il devra revenir.
    const opener = document.activeElement

    const focusables = () => Array.from(panel.querySelectorAll(FOCUSABLE))
      .filter(el => el.offsetParent !== null || el === document.activeElement)

    // Un `autoFocus` sur le champ de recherche suffit d'ordinaire ; ce repli
    // couvre le cas où le dialogue s'ouvre sans champ focalisable.
    if (!panel.contains(document.activeElement)) {
      (focusables()[0] ?? panel).focus?.()
    }

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    panel.addEventListener('keydown', onKeyDown)
    return () => {
      panel.removeEventListener('keydown', onKeyDown)
      // `isConnected` : ne pas rendre le focus à un bouton que le rendu a retiré.
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [ref, active])
}
